import { Link } from 'react-router-dom';

function Stat({ value, label }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
      <div
        className="text-3xl font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {value}
      </div>
      <div className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{label}</div>
    </div>
  );
}

export default function WhyIdentityMatters() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(37,99,235,0.18),transparent_40%),radial-gradient(circle_at_75%_35%,rgba(6,182,212,0.10),transparent_55%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Why Identity Matters
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Identity is the primary attack vector. Full stop.
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            The traditional security perimeter—firewalls, network segmentation, VPNs—is obsolete. In a world of cloud,
            remote work, APIs, and microservices, identity is the only consistent control point.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/architecture"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Explore the Architecture
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Request Assessment
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
        <div>
          <h2
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            The End of the Perimeter
          </h2>
          <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            If you control identity, you control access. If you control access, you control risk.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              {
                title: "Breaches",
                bullets: [
                  "Credential theft is the #1 attack vector",
                  "Lateral movement through compromised accounts",
                  "Data exfiltration via insider access",
                ],
              },
              {
                title: "Fraud",
                bullets: [
                  "Account takeover attacks",
                  "Synthetic identity fraud",
                  "Unauthorized transactions",
                ],
              },
              {
                title: "Regulatory penalties",
                bullets: ["GDPR, RBI, CERT-In, DPDP violations", "Failed audits", "License revocations"],
              },
              {
                title: "Operational disruption",
                bullets: [
                  "Ransomware via privileged access",
                  "Supply chain compromise",
                  "Business continuity failures",
                ],
              },
            ].map((b) => (
              <div key={b.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <div className="text-sm font-semibold text-[var(--text-primary)]">{b.title}</div>
                <ul className="mt-3 grid gap-2 text-sm text-[var(--text-secondary)]">
                  {b.bullets.map((li) => (
                    <li key={li} className="leading-6">
                      {li}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-8 backdrop-blur-xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              The Statistics
            </div>
            <div className="mt-5 grid gap-4">
              <Stat value="80%+" label="of breaches involve identity compromise" />
              <Stat value="74%" label="increase in credential-based attacks YoY" />
              <Stat value="$4.45M" label="average breach cost (IBM 2023)" />
              <Stat value="277 days" label="average breach lifecycle" />
            </div>
          </div>

          <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.02] p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Risk equation
            </div>
            <div className="mt-4 font-mono text-sm text-[var(--text-primary)]">
              IDENTITY compromised → ACCESS granted → ASSETS exposed → BUSINESS impacted
            </div>
            <div className="mt-3 text-sm text-[var(--text-secondary)]">
              Secure identity first. Everything else follows.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
